"use client";

import Translations from "@translate/components/ChatModal/ChatModalHeaderBar/ChatModalHeaderBar";
import { Icon, ospThemes } from "@ospweb/sdk-ui";
import { OspIcons } from "@ospweb/sdk-ui/src/Icon/OspIcons";
import { cn } from "utils";
import {
  ChatTabMoreMenu,
  type ChatTabMoreMenuItemId,
} from "@components/ChatTabComp/ChatTabMoreMenu";
import type { ChatTabMoreMenuItemUiStateMap } from "@utils/chatTabMoreMenuItemUiState";
import { I18nUtil } from "@utils/i18n";

const I18n = I18nUtil.createScoped(Translations);

/** 小屏 modal header 悬浮高度（pt-5 + pb-4 + 24px icon），与滚动区 pt 对齐 */
export const CHAT_MODAL_MOBILE_HEADER_HEIGHT_PX = 56;

const CHAT_MODAL_HEADER_ACTION_SHADOW =
  "shadow-[0_2px_20px_0_rgba(0,0,0,0.12)]";

type ChatModalHeaderBarMenuItem = {
  icon: string;
  label: string;
  size?: number;
  disabled?: boolean;
  color?: string;
  onClick?: () => void;
};

export type ChatModalHeaderBarProps = {
  isMobile: boolean;
  hasMessages: boolean;
  onClose: () => void;
  onNewChatClick: () => void;
  moreMenuItemUi?: ChatTabMoreMenuItemUiStateMap;
  onMoreMenuItemClick?: (itemId: ChatTabMoreMenuItemId) => void;
  /** 大屏 modal 沿用平铺图标菜单 */
  menuItems?: ChatModalHeaderBarMenuItem[];
};

export const ChatModalHeaderBar = ({
  isMobile,
  hasMessages,
  onClose,
  onNewChatClick,
  moreMenuItemUi,
  onMoreMenuItemClick,
  menuItems,
}: ChatModalHeaderBarProps) => {
  return (
    <div
      className={cn(
        "flex items-center justify-between pt-5 pb-4 px-4 md:pt-4 md:px-6",
        isMobile &&
          "absolute top-0 pt-safe left-0 right-0 z-20 pointer-events-none",
      )}
    >
      {isMobile && (
        <div
          className="absolute inset-x-0 top-0 h-fit pointer-events-none"
          aria-hidden
        >
          {/* background_solid_secondary (#f9f9f3)，顶部略透明 → 底部全透明 */}
          <div
            className="h-safe-top"
            style={{
              background:
                "linear-gradient(180deg, rgba(249, 249, 243, 0.94) 0%, rgba(249, 249, 243, 0.94) 100%)",
            }}
          />
          <div
            className="h-20"
            style={{
              background:
                "linear-gradient(180deg, rgba(249, 249, 243, 0.94) 0%, rgba(249, 249, 243, 0) 100%)",
            }}
          />
        </div>
      )}
      <div
        className={cn(
          "relative flex w-full items-center justify-between py-2",
          isMobile && "z-10",
        )}
      >
        {isMobile ? (
          <>
            <button
              type="button"
              data-openreplay-label="chatbox_chatmodalheaderbar_close"
              className={cn(
                "pointer-events-auto flex size-10 shrink-0 items-center justify-center rounded-full bg-background_solid_neutral_primary",
                CHAT_MODAL_HEADER_ACTION_SHADOW,
              )}
              onClick={onClose}
              aria-label="Close"
            >
              <Icon name="close" size={24} className="text-content_primary" />
            </button>
            <div
              className={cn(
                "pointer-events-auto flex items-center gap-4 rounded-2 bg-background_solid_neutral_primary px-4 py-2",
                CHAT_MODAL_HEADER_ACTION_SHADOW,
              )}
            >
              {hasMessages ? (
                <button
                  type="button"
                  data-openreplay-label="chatbox_chatmodalheaderbar_add"
                  className="inline-flex size-6 items-center justify-center"
                  onClick={onNewChatClick}
                  aria-label="New Chat"
                >
                  <Icon
                    name="new-chat"
                    size={24}
                    className="text-content_primary"
                  />
                </button>
              ) : null}
              <ChatTabMoreMenu
                className="gap-0"
                itemUi={moreMenuItemUi}
                onItemClick={onMoreMenuItemClick}
              />
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center gap-1">
              <div className="headline_regular_s text-content_primary">
                {I18n.t("Discuss with Edggy")}
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-3">
              {menuItems?.map((item) => {
                if (item.disabled) {
                  return undefined;
                }
                return (
                  <button
                    type="button"
                    data-openreplay-label="chatbox_chatmodalheaderbar_menuitem"
                    key={item.label}
                    className="inline-flex size-6 cursor-pointer items-center justify-center"
                    onClick={() => {
                      item.onClick?.();
                    }}
                    data-tracking-scope="chat-menu-item"
                    data-tracking-name={item.label}
                    aria-label={item.label}
                  >
                    <OspIcons
                      name={item.icon}
                      size={item.size ?? 20}
                      color={item.color || ospThemes.colors.content_primary}
                    />
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
